// ANGULAR
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { Title } from '@angular/platform-browser';
// SERVICES
import { AlertService } from '@services/alert.service';
import { GlobalService } from '@services/global.service';
import { BackupsService } from '@api-requests-services/backups.service';
import { EdicionService } from '@api-requests-services/edicion.service';
// MODELS
import { Backup, PostRestoreBackupRequest, PostPutBackupRequest, BackupOption } from '@models/backups.model';
// RXJS
import { finalize } from 'rxjs';

export enum Views {
  Home = 'home',
  Backups = 'backups',
  CreatingBackup = 'creatingBackup',
  EditingBackups = 'editingBackups',
  RestoringBackups = 'restoringBackups',
  BackupComparison = 'backupComparison',
}

@Component({
  selector: 'app-backups',
  templateUrl: './backups.component.html',
  styleUrl: './backups.component.css',
  imports: [
    CommonModule,
    FormsModule
  ]
})
export class BackupsComponent {
  views = Views;
  currentView: string = Views.Home;
  backups: Backup[];
  comment: string;
  protegido: boolean = false;
  backupSearched: Backup;
  backupDateToSearch: string;
  backupNotFound: boolean = false;
  backupSelected: Backup | null = null;
  postRestoreBackupRequest: PostRestoreBackupRequest = {
    actualizaciones: true,
    aplicaciones: true,
    connectionStrings: true,
    grupos: true,
    m2Ms: true,
    iPs: true,
    secretos: true,
    tokensCfg: true
  };
  // Variables de comparación de backups
  backupsOptions: BackupOption[] = [];
  item1: string | null = null;
  item2: string | null = null;
  showCompareBackupsError: boolean = false;
  backupComparison: string = '';
  backupComparisonInProgress: boolean = false;

  constructor(
    private alertService: AlertService,
    public globalService: GlobalService,
    private backupsService: BackupsService,
    private edicionService: EdicionService,
    private title: Title,
    private datePipe: DatePipe
  ) {
    this.title.setTitle('Oráculo - Backups');
  }

  datePipeTransform(isoDate?: Date): string {
    if (!!isoDate) {
      return this.datePipe.transform(isoDate, 'dd/MM/yyyy HH:mm:ss') || '';
    } else {
      return '';
    }
  }

  startBackupPreview(backup: Backup): void {
    this.globalService.setBackupToPreview(backup);
  }

  selectView(view: string, backupSelected?: Backup): void {
    if (!!backupSelected) { this.backupSelected = backupSelected; }
    this.currentView = view;

    switch (this.currentView) {
      case Views.Home:
        this.backups = [];
        this.comment = '';
        this.protegido = false;
        break;
      case Views.Backups:
        this.getBackups();
        this.comment = '';
        this.protegido = false;
        this.backupComparison = '';
        break;
      case Views.CreatingBackup:
        this.comment = '';
        this.protegido = false;
        break;
      case Views.EditingBackups:
        this.comment = this.backupSelected?.comentario ?? '';
        this.protegido = !!this.backupSelected?.protegido ? true : false;
        break;
      case Views.RestoringBackups:
        this.postRestoreBackupRequest = {
          actualizaciones: true,
          aplicaciones: true,
          connectionStrings: true,
          grupos: true,
          m2Ms: true,
          iPs: true,
          secretos: true,
          tokensCfg: true
        }
        break;
      case Views.BackupComparison:
        this.getBackupsOptions();
        break;
    }
  }

  getBackups(): void {
    this.backupsService.getBackups().subscribe(
      backups => {
        if (backups) {
          // console.log('Backups: ', backups); 
          this.backups = backups;
        }
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error getting Backups:', error);
        }
      }
    );
  }

  createBackup(): void {
    const postPutBackupRequest: PostPutBackupRequest = {
      comentario: this.comment,
      protegido: this.protegido
    };

    this.backupsService.postCreateBackup(postPutBackupRequest).subscribe(
      () => {
        this.selectView(Views.Backups);
        this.alertService.showAutoCloseMessage(`¡Backup creado!`, ``, "success", 1200);
      },
      error => {
        if (error.error === this.globalService.editModeDisabledMsg) {
          this.alertService.swalEditingNotEnabled();
        } else {
          this.alertService.swalError(error.error);
          console.error('Error creating Backup:', error);
        }
      }
    );
  }

  getBackup(): void {
    this.backupSearched = {
      fecha: new Date('2024-01-01T00:00:00'),
      ultimoCambio: new Date('2024-01-01T00:00:00'),
      comentario: '',
      protegido: false,
      edicion: false
    };

    if (this.backupDateToSearch) {
      this.backupsService.getBackup(this.backupDateToSearch).subscribe(
        backup => {
          if (backup) {
            // console.log('Backup: ', backup);
            this.backupNotFound = false;
            this.backupSearched = backup;
          }
        },
        error => {
          if (error.error === this.globalService.editModeDisabledMsg) {
            this.alertService.swalEditingNotEnabled();
          } else {
            if (error.status === 400) {
              this.backupNotFound = true;
            } else {
              this.backupNotFound = false;
            }
            this.alertService.swalError(error.error);
            console.error('Error getting Backup:', error);
          }
        }
      );
    } else {
      this.backupNotFound = true;
    }
  }

  editBackup(): void {
    const postPutBackupRequest: PostPutBackupRequest = {
      comentario: this.comment,
      protegido: this.protegido
    }

    if (!!this.backupSelected) {
      this.alertService.showConfirmAlert(
        `Editar Backup`,
        `¿Seguro que quieres editar el backup con fecha "${this.datePipeTransform(this.backupSelected.fecha)}"?`
      ).then((result) => {
        if (result.isConfirmed && !!this.backupSelected) {
          this.backupsService.putEditBackup(this.backupSelected.fecha.toString(), postPutBackupRequest).subscribe(
            () => {
              this.alertService.showAutoCloseMessage(`¡Backup editado!`, ``, "success", 1200);
              this.getBackups();
              this.selectView(Views.Backups);
            },
            error => {
              if (error.error === this.globalService.editModeDisabledMsg) {
                this.alertService.swalEditingNotEnabled();
              } else {
                this.alertService.swalError(error.error);
                console.error('Error editing Backup:', error);
              }
            }
          );
        }
      });
    }
  }

  deleteBackup(backup: Backup): void {
    this.alertService.showConfirmAlert(
      `Eliminar Backup`,
      `¿Seguro que quieres eliminar el backup con fecha "${this.datePipeTransform(backup.fecha)}"?`
    ).then((result) => {
      if (result.isConfirmed) {
        this.backupsService.deleteBackup(backup.fecha.toString()).subscribe(
          () => {
            this.alertService.showAutoCloseMessage(`¡Backup borrado!`, ``, "success", 1200);
            this.getBackups();
          },
          error => {
            if (error.error === this.globalService.editModeDisabledMsg) {
              this.alertService.swalEditingNotEnabled();
            } else {
              this.alertService.swalError(error.error);
              console.error('Error deleting Backup:', error);
            }
          }
        );
      }
    });
  }

  restoreBackup(): void {
    if (!!this.backupSelected) {
      this.alertService.showConfirmAlert(
        `Restaurar Backup`,
        `¿Seguro que quieres restaurar el backup con fecha "${this.datePipeTransform(this.backupSelected.fecha)}"?`
      ).then((result) => {
        if (result.isConfirmed && !!this.backupSelected) {
          this.backupsService.postRestaurarBackup(this.backupSelected.fecha.toString(), this.postRestoreBackupRequest).subscribe(
            () => {
              this.alertService.showAutoCloseMessage(`¡Backup restaurado!`, ``, "success", 1200);
              this.selectView(Views.Backups);
            },
            error => {
              if (error.error === this.globalService.editModeDisabledMsg) {
                this.alertService.swalEditingNotEnabled();
              } else {
                this.alertService.swalError(error.error);
                console.error('Error restoring Backup:', error);
                this.backupNotFound = false;
              }
            }
          );
        }
      });
    }
  }

  getBackupsOptions(): void {
    this.edicionService.getModoEdicion().subscribe(
      editMode => {
        if (editMode) {
          // console.log('Modo edición: ', editMode);
          this.backupsOptions = [
            { key: 'Actual', value: 'null' }, 
            ...(editMode.modoEdicion ? [{ key: 'Edición', value: '' }] : []), 
            ...this.backups.map(backup => ({ key: this.datePipeTransform(new Date(backup.fecha)), value: new Date(backup.fecha).toISOString() }))
          ];

          this.item1 = !!this.backupSelected ? new Date(this.backupSelected.fecha).toISOString() : new Date().toISOString();
          this.item2 = this.backupsOptions.length > 0 ? this.backupsOptions[0].value : null;
        }
      },
      error => {
        this.alertService.swalError(error.error);
        console.error('Error getting Edit Mode:', error);
      }
    );
  }

  compareBackups(): void {
    this.backupComparison = '';
    this.backupComparisonInProgress = true;

    if (this.item1 === this.item2 || this.item1 === null || this.item1 === '') {
      this.showCompareBackupsError = true;
    } else {
      this.showCompareBackupsError = false;

      this.backupsService.getBackupsComparar(this.item1, this.item2 !== 'null' ? this.item2 : null)
        .pipe(finalize(() => this.backupComparisonInProgress = false))
        .subscribe(
          backupComparison => {
            if (backupComparison) {
              // console.log('Comparación de backups: ', backupComparison);
              const backupComparisonFormatted: string = `<pre>${JSON.stringify(backupComparison, null, 2)}</pre>`;
              this.backupComparison = backupComparisonFormatted;
            }
          },
          error => {
            this.alertService.swalError(error.error);
            console.error('Error getting Backups Comparison:', error);
          }
        );
    }
  }
}
